import { useMemo, useState } from 'react';
import { Plus, RotateCcw, Search, SlidersHorizontal, Download, FileText } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { RowMenu } from '../../components/RowMenu';
import {
  Button, IconButton, Input, Skeleton,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuCheckboxItem,
} from '@vnetwork/ui/primitives';
import { LiveConsoleBreadcrumbs } from '../LiveConsoleBreadcrumbs';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useLmsSettingsTemplates,
  useDeleteLmsSettingsTemplate,
} from '@/api/media/live-settings-templates';
import type { LmsSettingsTemplate } from '@/api/media/live-settings-templates-types';
import { SettingsTemplateCreateDialog } from './SettingsTemplateCreateDialog';
import { SettingsTemplateDetailDialog } from './SettingsTemplateDetailDialog';
import { fmtTs } from './settings-template-utils';
import styles from './SettingsTemplates.module.css';

// Columns the user can toggle via "Display options" (Domain + actions fixed).
const TOGGLEABLE = [
  { id: 'id', label: 'ID' },
  { id: 'app', label: 'App' },
  { id: 'stream', label: 'Stream' },
  { id: 'created', label: 'Created' },
  { id: 'updated', label: 'Updated' },
];
const DEFAULT_VISIBLE: Record<string, boolean> = { id: true, app: true, stream: true, created: true, updated: true };

/** Download the given templates as a CSV file. */
function exportCsv(rows: LmsSettingsTemplate[]) {
  const head = ['id', 'domain', 'app', 'stream', 'created', 'updated'];
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows
    .map((r) => [r.id, r.domain, r.app, r.stream, fmtTs(r.created), fmtTs(r.updated)].map(esc).join(','))
    .join('\n');
  const blob = new Blob([`${head.join(',')}\n${body}`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `live-domain-templates-${rows.length}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Component ─────────────────────────────────────────────────────────────────

export interface LiveSettingsTemplatesProps {
  serviceId: string;
}

/**
 * Domain Templates list — nav label "Domain Templates" per legacy.
 * Create / edit / detail / delete via dialogs (no route change).
 */
export function LiveSettingsTemplates({ serviceId }: LiveSettingsTemplatesProps) {
  const { show } = useToast();

  const { data, isLoading, refetch } = useLmsSettingsTemplates(serviceId);
  const deleteMut = useDeleteLmsSettingsTemplate(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Template deleted' }); refetch(); },
    onError: (e) => show({ tone: 'danger', title: e.message || 'Failed to delete template' }),
  });

  const templates = data?.items ?? [];

  // Dialog + toolbar state
  const [query, setQuery] = useState('');
  const [visible, setVisible] = useState<Record<string, boolean>>(DEFAULT_VISIBLE);
  const [createOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LmsSettingsTemplate | null>(null);

  // Client-side search over the full list (domain / app / stream).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) =>
      [t.domain, t.app, t.stream].some((v) => (v ?? '').toLowerCase().includes(q)),
    );
  }, [templates, query]);

  const columns = useMemo<ColumnDef<LmsSettingsTemplate, unknown>[]>(() => {
    const all: Record<string, ColumnDef<LmsSettingsTemplate, unknown>> = {
      id: {
        id: 'id',
        header: 'ID',
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); setDetailId(row.original.id); }}
            className={styles.idBtn}
          >
            {row.original.id}
          </Button>
        ),
        size: 70,
      },
      app: {
        id: 'app',
        header: 'App',
        cell: ({ row }) => <span className={styles.cellMuted}>{row.original.app || '—'}</span>,
      },
      stream: {
        id: 'stream',
        header: 'Stream',
        cell: ({ row }) => <span className={styles.cellMuted}>{row.original.stream || '—'}</span>,
      },
      created: {
        id: 'created',
        header: 'Created',
        cell: ({ row }) => <span className={styles.cellTimestamp}>{fmtTs(row.original.created)}</span>,
        size: 180,
      },
      updated: {
        id: 'updated',
        header: 'Updated',
        cell: ({ row }) => <span className={styles.cellTimestamp}>{fmtTs(row.original.updated)}</span>,
        size: 180,
      },
    };
    const domainCol: ColumnDef<LmsSettingsTemplate, unknown> = {
      id: 'domain',
      header: 'Domain',
      cell: ({ row }) => <span className={styles.cellDomain}>{row.original.domain || '—'}</span>,
    };
    const actionsCol: ColumnDef<LmsSettingsTemplate, unknown> = {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <RowMenu
          items={[
            { label: 'View', onSelect: () => setDetailId(row.original.id) },
            { label: 'Edit', onSelect: () => setEditId(row.original.id) },
            { label: 'Delete', danger: true, onSelect: () => setDeleteTarget(row.original) },
          ]}
        />
      ),
      size: 60,
    };
    return [domainCol, ...TOGGLEABLE.filter((c) => visible[c.id]).map((c) => all[c.id]), actionsCol];
  }, [visible]);

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<LiveConsoleBreadcrumbs trail={[{ label: 'Domain Templates' }]} />}
        title={`Domain Templates${templates.length ? ` (${templates.length})` : ''}`}
        description="Manage domain, app, and stream mappings for live stream settings."
        actions={
          <Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>
            Create Template
          </Button>
        }
      />

      <Toolbar
        start={
          <Input
            startSlot={<Search size={15} />}
            placeholder="Search by domain, app, or stream"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        }
        end={
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                  Display options
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                {TOGGLEABLE.map((c) => (
                  <DropdownMenuCheckboxItem
                    key={c.id}
                    checked={visible[c.id]}
                    onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.id]: v === true }))}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {c.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="secondary"
              startIcon={<Download size={15} />}
              disabled={filtered.length === 0}
              onClick={() => exportCsv(filtered)}
            >
              Export
            </Button>
            <IconButton variant="secondary" aria-label="Refresh" onClick={() => refetch()} disabled={isLoading}>
              <RotateCcw size={15} />
            </IconButton>
          </>
        }
      />

      {isLoading ? (
        <div className={styles.skeletonGrid}>
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="rect" height={44} />)}
        </div>
      ) : templates.length === 0 ? (
        <EmptyState
          icon={<FileText size={20} />}
          title="No templates yet"
          description="Create a domain template to configure live stream settings."
          actions={<Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>Create Template</Button>}
        />
      ) : (
        <DataTable
          data={filtered}
          columns={columns}
          getRowId={(r) => String(r.id)}
          onRowClick={(row) => setDetailId(row.id)}
          aria-label="Domain templates"
          emptyState="No templates match your search."
          footer={filtered.length ? `Showing ${filtered.length} of ${templates.length}` : undefined}
        />
      )}

      {/* Create / edit dialog */}
      <SettingsTemplateCreateDialog
        open={createOpen || editId !== null}
        serviceId={serviceId}
        templateId={editId ?? undefined}
        onClose={() => { setCreateOpen(false); setEditId(null); }}
        onSuccess={() => refetch()}
      />

      {/* Detail dialog */}
      {detailId !== null && (
        <SettingsTemplateDetailDialog
          open
          serviceId={serviceId}
          id={detailId}
          onClose={() => setDetailId(null)}
        />
      )}

      {/* Delete confirm */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete Domain Template"
        description={`Permanently delete template "${deleteTarget?.domain}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        requireText={deleteTarget?.domain}
        loading={deleteMut.isPending}
        onConfirm={() => { if (deleteTarget) deleteMut.mutate(deleteTarget.id); }}
      />
    </Container>
  );
}
